<template>
  <div class="behaviour-settings-automated-points">
    <h2 class="sr-only">
      {{ behaviourTerminology.automatedPointsSectionTitle }}
    </h2>
    <output class="sr-only" aria-live="polite" aria-atomic="true">{{
      liveAnnouncement
    }}</output>
    <Loader :is-processing="state.isLoading || state.isSaving" />
    <div
      class="d-flex flex-wrap justify-content-end gap-2 mb-3"
      data-testid="behaviour-automated-points-actions"
    >
      <BaseButton
        rounded
        type="button"
        data-testid="behaviour-automated-points-cancel"
        :is-disabled="state.isLoading || state.isSaving"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </BaseButton>
      <BaseButton
        solid
        rounded
        type="button"
        data-testid="behaviour-automated-points-save"
        :is-disabled="state.isLoading || state.isSaving || !isDirty"
        @click="onSave"
      >
        {{ $t("behaviour_settings.save") }}
      </BaseButton>
    </div>
    <CAlert
      v-if="state.response.message"
      dismissible
      class="mb-3"
      :color="state.response.type"
      :show="!!state.response.message"
      role="status"
      aria-live="polite"
      :data-testid="`behaviour-automated-points-${state.response.type}`"
      @close="resetResponse"
    >
      {{ state.response.message }}
    </CAlert>
    <div
      class="behaviour-automated-points-panel rounded bg-white p-4"
      data-testid="behaviour-automated-points-panel"
    >
      <AutomatedPointsRuleSection
        v-model="form[ruleKeys.ON_TIME_FOR_SCHOOL]"
        :field-errors="schoolFieldErrors"
        :show-errors="state.submitAttempted"
        :title="behaviourTerminology.onTimeForSchool"
        :description="behaviourTerminology.onTimeForSchoolDescription"
        :frequency-label="$t('behaviour_settings.frequency_label')"
        :core-value-label="behaviourTerminology.coreValue"
        :points-awarded-label="behaviourTerminology.pointsAwardedLabel"
        :core-value-placeholder="
          $t('behaviour_settings.core_value_select_placeholder', {
            coreValue: behaviourTerminology.coreValue
          })
        "
        :points-placeholder="$t('behaviour_settings.points_placeholder')"
        :frequency-options="frequencyOptions"
        :switch-aria-label="
          $t('behaviour_settings.automated_rule_toggle_a11y', {
            rule: behaviourTerminology.onTimeForSchool
          })
        "
        test-id-prefix="behaviour-automated-school"
        heading-id="behaviour-automated-school-heading"
      />
      <hr class="behaviour-automated-points-divider my-4" />
      <AutomatedPointsRuleSection
        v-model="form[ruleKeys.ON_TIME_FOR_CLASS]"
        :field-errors="classFieldErrors"
        :show-errors="state.submitAttempted"
        :title="behaviourTerminology.onTimeForClass"
        :description="behaviourTerminology.onTimeForClassDescription"
        :frequency-label="$t('behaviour_settings.frequency_label')"
        :core-value-label="behaviourTerminology.coreValue"
        :points-awarded-label="behaviourTerminology.pointsAwardedLabel"
        :core-value-placeholder="
          $t('behaviour_settings.core_value_select_placeholder', {
            coreValue: behaviourTerminology.coreValue
          })
        "
        :points-placeholder="$t('behaviour_settings.points_placeholder')"
        :frequency-options="frequencyOptions"
        :switch-aria-label="
          $t('behaviour_settings.automated_rule_toggle_a11y', {
            rule: behaviourTerminology.onTimeForClass
          })
        "
        test-id-prefix="behaviour-automated-class"
        heading-id="behaviour-automated-class-heading"
      />
    </div>
  </div>
</template>

<script>
import behaviourAutomatedPointsConstants from "@/constants/behaviourAutomatedPointsConstants.js"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import {
  cloneAutomatedPointsSettings,
  createDefaultAutomatedPointsSettings,
  normalizeAutomatedPointsFormValues,
  parseAutomatedPointsApiError,
  validateAutomatedPointsForm
} from "@/helpers/behaviourAutomatedPointsForm.js"
import AutomatedPointsRuleSection from "@/v3components/Behaviour/AutomatedPointsRuleSection.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import { computed, onMounted, reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourSettingsAutomatedPointsTab",
  components: {
    AutomatedPointsRuleSection,
    BaseButton,
    Loader
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const liveAnnouncement = ref("")
    const savedSnapshot = ref(null)
    /**
     * Lookup of `id → name` used to resolve `coreValueName` when the
     * automated-points GET response doesn't embed the `core_value` relation.
     */
    let coreValueLookup = new Map()

    const ruleKeys = behaviourAutomatedPointsConstants.RULE_KEYS

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const form = reactive(createDefaultAutomatedPointsSettings())

    const state = reactive({
      isLoading: false,
      isSaving: false,
      submitAttempted: false,
      response: {
        type: "",
        message: ""
      }
    })

    const resetResponse = () => {
      state.response = { type: "", message: "" }
    }

    const setSuccessResponse = (message) => {
      state.response = {
        type: "success",
        message: message || "Success!"
      }
    }

    const setErrorResponse = (message) => {
      state.response = {
        type: "danger",
        message: message || t("common.something_went_wrong")
      }
    }

    const frequencyOptions = computed(() => [
      {
        label: t("behaviour_settings.frequency_daily"),
        value: behaviourAutomatedPointsConstants.FREQUENCY.DAILY
      },
      {
        label: t("behaviour_settings.frequency_weekly"),
        value: behaviourAutomatedPointsConstants.FREQUENCY.WEEKLY
      },
      {
        label: t("behaviour_settings.frequency_monthly"),
        value: behaviourAutomatedPointsConstants.FREQUENCY.MONTHLY
      }
    ])

    const isDirty = computed(() => {
      if (!savedSnapshot.value) return false
      return JSON.stringify(form) !== JSON.stringify(savedSnapshot.value)
    })

    const validationResult = computed(() => validateAutomatedPointsForm(form))

    const showValidationSummary = computed(
      () => state.submitAttempted && !validationResult.value.valid
    )

    const mapRuleFieldErrors = (ruleKey) => {
      const raw = validationResult.value.errors?.[ruleKey] || {}
      const bt = behaviourTerminology.value
      const out = {}
      if (raw.frequency) {
        out.frequency = t(
          "behaviour_settings.automated_points_error_required_field",
          {
            field: t("behaviour_settings.frequency_label")
          }
        )
      }
      if (raw.coreValueId) {
        out.coreValueId = t(
          "behaviour_settings.automated_points_error_required_field",
          { field: bt.coreValue }
        )
      }
      if (raw.pointsAwarded) {
        out.pointsAwarded = t(
          "behaviour_settings.automated_points_error_required_field",
          { field: bt.pointsAwardedLabel }
        )
      }
      return out
    }

    const schoolFieldErrors = computed(() =>
      mapRuleFieldErrors(ruleKeys.ON_TIME_FOR_SCHOOL)
    )
    const classFieldErrors = computed(() =>
      mapRuleFieldErrors(ruleKeys.ON_TIME_FOR_CLASS)
    )

    const announce = (message) => {
      liveAnnouncement.value = ""
      if (!message) return
      requestAnimationFrame(() => {
        liveAnnouncement.value = message
      })
    }

    /**
     * Fills missing `coreValueName` on each rule using the cached lookup, so
     * the dropdown can render the saved name even when the GET response omits
     * the `core_value` relation.
     */
    const enrichFormWithCoreValueNames = () => {
      if (!coreValueLookup.size) return
      ;[ruleKeys.ON_TIME_FOR_SCHOOL, ruleKeys.ON_TIME_FOR_CLASS].forEach(
        (ruleKey) => {
          const rule = form[ruleKey]
          if (!rule) return
          const id = rule.coreValueId
          if (id == null || id === "") return
          if (rule.coreValueName) return
          const info = coreValueLookup.get(String(id))
          if (info?.name) rule.coreValueName = info.name
        }
      )
    }

    const loadCoreValueLookup = async () => {
      try {
        const { rows } = await store.dispatch(
          "behaviourSettings/fetchCoreValuesList",
          {
            page: 1,
            perPage: behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE,
            search: "",
            statusFilter: "all"
          }
        )
        const next = new Map()
        ;(rows || []).forEach((row) => {
          if (row?.id == null) return
          next.set(String(row.id), { name: String(row.name ?? "") })
        })
        coreValueLookup = next
      } catch {
        /* lookup is best-effort; rule sections will still render */
      }
    }

    const loadSettings = async () => {
      try {
        state.isLoading = true
        resetResponse()
        await store.dispatch("behaviourSettings/fetchBehaviourTerminology")
        await loadCoreValueLookup()
        const data = await store.dispatch(
          "behaviourSettings/fetchAutomatedPointsSettings"
        )
        Object.assign(form, cloneAutomatedPointsSettings(data))
        normalizeAutomatedPointsFormValues(form)
        enrichFormWithCoreValueNames()
        savedSnapshot.value = cloneAutomatedPointsSettings(form)
      } catch (error) {
        setErrorResponse(
          parseAutomatedPointsApiError(error) ||
            t("behaviour_settings.automated_points_load_error")
        )
        Object.assign(form, createDefaultAutomatedPointsSettings())
        savedSnapshot.value = cloneAutomatedPointsSettings(form)
      } finally {
        state.isLoading = false
      }
    }

    const onCancel = () => {
      if (state.isLoading || state.isSaving || !savedSnapshot.value) return
      state.submitAttempted = false
      resetResponse()
      Object.assign(form, cloneAutomatedPointsSettings(savedSnapshot.value))
      announce(t("behaviour_settings.automated_points_cancelled"))
    }

    const onSave = async () => {
      if (state.isLoading || state.isSaving) return
      state.submitAttempted = true
      resetResponse()

      const { valid } = validationResult.value
      if (!valid) {
        const bt = behaviourTerminology.value
        const message = t(
          "behaviour_settings.automated_points_validation_error",
          {
            coreValuesLower: bt.coreValuesLower,
            points: bt.points
          }
        )
        setErrorResponse(message)
        announce(message)
        return
      }

      try {
        state.isSaving = true
        const data = await store.dispatch(
          "behaviourSettings/upsertAutomatedPointsSettings",
          cloneAutomatedPointsSettings(form)
        )
        Object.assign(form, cloneAutomatedPointsSettings(data))
        normalizeAutomatedPointsFormValues(form)
        enrichFormWithCoreValueNames()
        savedSnapshot.value = cloneAutomatedPointsSettings(form)
        state.submitAttempted = false
        const savedText = t("behaviour_settings.automated_points_saved")
        setSuccessResponse(savedText)
        announce(savedText)
      } catch (error) {
        const message =
          parseAutomatedPointsApiError(error) ||
          t("behaviour_settings.automated_points_save_error")
        setErrorResponse(message)
        announce(message)
      } finally {
        state.isSaving = false
      }
    }

    onMounted(() => {
      loadSettings()
    })

    return {
      behaviourTerminology,
      form,
      state,
      ruleKeys,
      frequencyOptions,
      isDirty,
      schoolFieldErrors,
      classFieldErrors,
      showValidationSummary,
      liveAnnouncement,
      onCancel,
      onSave,
      resetResponse
    }
  }
}
</script>
